import { jsPDF } from "jspdf";
import T, {
  COL_GUTTER,
  COL_W,
  CONTENT_W,
  FONT,
  MARGIN,
  PAGE,
} from "./submissionPdfTokens";
import { EMPTY } from "./submissionPdfFormat";

/**
 * The drawing primitives for the submission PDF — document header, metrics
 * band, ruled sections, two-column field rows, the economics bars, the
 * documents table, and the page footers.
 *
 * Anatomy per §3: a section is `[uppercase title · optional count]` over a body,
 * and its header carries a solid 1pt ink underline — the strongest rule on the
 * page, so sections stay findable when the document is skimmed on paper.
 */

const LINE_H = 11.5; // one wrapped line of a field value
const ROW_PAD = 4.5; // §1.3 print field-row padding
const LABEL_W = 96; // label column inside a packed column

/**
 * A value in a packed column has ~146pt to wrap into, so genuinely long prose
 * (an exit strategy, an experience summary) would run off the bottom of the
 * page as a single unbreakable row. Column rows are capped and the prose is
 * printed full-width by `blockField` instead, which paginates line by line.
 */
const MAX_COL_LINES = 8;

// Vertical rhythm between blocks. SECTION_LEAD is the air above a section
// title; BLOCK_TAIL is the air a body leaves under itself. Together they set
// how far apart two sections sit — the pair is the one place to tune it.
const SECTION_LEAD = 16;
const BLOCK_TAIL = 20;

export default class SubmissionPdfDoc {
  constructor({ identity }) {
    this.pdf = new jsPDF({ unit: "pt", format: "letter" });
    this.identity = identity;
    this.y = MARGIN;
    // Where content starts on the current page, so `section` knows whether it
    // is the first thing on the page (no lead) or following a body (lead).
    this.pageTop = MARGIN;
    // Drawn by ensure() when content spills onto an unplanned page.
    this.continuation = null;
  }

  // ── type helpers ────────────────────────────────────────────────────────
  sans(size, style = "normal") {
    this.pdf.setFont(FONT.sans, style).setFontSize(size);
    return this;
  }

  mono(size, style = "normal") {
    this.pdf.setFont(FONT.mono, style).setFontSize(size);
    return this;
  }

  ink(color = T.ink) {
    this.pdf.setTextColor(...color);
    return this;
  }

  /**
   * Wrap to `width` and cut to `maxLines`, ellipsing the last line so a long
   * value announces that it was truncated instead of silently losing its tail.
   * Call it with the font already set — jsPDF measures in the current face.
   */
  clampLines(value, width, maxLines) {
    const lines = this.pdf.splitTextToSize(String(value ?? ""), width);
    if (lines.length <= maxLines) return lines;

    const kept = lines.slice(0, maxLines);
    let last = `${kept[maxLines - 1]} …`;
    while (last.length > 1 && this.pdf.getTextWidth(last) > width) {
      last = `${last.slice(0, -3).trimEnd()} …`;
    }
    kept[maxLines - 1] = last;
    return kept;
  }

  // ── page flow ───────────────────────────────────────────────────────────
  /** Deliberate page break — the spec splits by meaning, not overflow. */
  break(continuationHeader) {
    this.pdf.addPage();
    this.y = MARGIN;
    if (continuationHeader) this.compactHeader(continuationHeader);
    this.pageTop = this.y;
  }

  /** Safety net for variable-length blocks (many properties, many documents). */
  ensure(needed) {
    if (this.y + needed <= PAGE.h - MARGIN - 24) return;
    this.break(this.continuation);
  }

  // ── headers ─────────────────────────────────────────────────────────────
  /**
   * The letterhead row — logo left, "what this document is" right, the way the
   * screen header reads. No status here: it's a workflow state, it's already a
   * field in Submission & signature, and on a printed document it read as a
   * stamp on the agreement rather than a note about the queue.
   */
  docHeader({ eyebrow, title, subtitle, refNo, logo }) {
    const right = MARGIN + CONTENT_W;
    const topY = this.y;

    const logoH = logo ? this.letterhead(logo) : 0;
    const rowH = Math.max(logoH, 18);

    // Optically centred against the logo rather than hung from the top of the
    // row: the two sit on the same line, so they should share a middle.
    const EYEBROW_SIZE = 10;
    this.sans(EYEBROW_SIZE, "bold").ink(T.brand);
    this.pdf.text(eyebrow, right, topY + rowH / 2 + EYEBROW_SIZE * 0.35, {
      align: "right",
    });

    // The taller of the two sides sets the row height, so a short logo can't
    // let the title ride up into the document type. The +34 is the gap between
    // the letterhead row and the title row — they are two separate things and
    // were reading as one stack.
    this.y = topY + rowH + 34;

    // The borrower's name titles the document, but it is not the loudest thing
    // on the page — the agreement is. 14pt keeps it a heading rather than a
    // banner, and leaves long names on one line.
    this.sans(14, "bold").ink(T.ink);
    this.pdf.text(title, MARGIN, this.y);

    if (refNo != null) {
      this.mono(11, "bold").ink(T.slate);
      this.pdf.text(`#${refNo}`, right, this.y, { align: "right" });
    }
    this.y += 14;

    if (subtitle) {
      // Wrapped explicitly rather than via `maxWidth`: jsPDF paints the extra
      // lines downward without telling us, so a long product · purpose · address
      // subtitle used to run straight through the rule below it.
      this.sans(10).ink(T.slate);
      const lines = this.clampLines(subtitle, CONTENT_W - 120, 2);
      this.pdf.text(lines, MARGIN, this.y);
      this.y += 13 * lines.length;
    }

    // The only 2pt rule in the system: "start of document".
    this.y += 6;
    this.pdf.setDrawColor(...T.ink).setLineWidth(2);
    this.pdf.line(MARGIN, this.y, right, this.y);
    this.y += 24;
    this.pageTop = this.y;
  }

  /**
   * The caller's logo, top-left, scaled to fit a 104×24pt box — a mark, not a
   * banner. Takes a data URL (or anything else jsPDF's addImage accepts): a
   * remote URL can't be read synchronously, so callers resolve theirs first;
   * see submissionPdfLogo.js. Returns the height it drew, or 0 — a logo that
   * won't decode is skipped rather than failing the document.
   */
  letterhead(logo) {
    const MAX_W = 104;
    const MAX_H = 24;
    try {
      const props = this.pdf.getImageProperties(logo);
      const scale = Math.min(MAX_W / props.width, MAX_H / props.height);
      const w = props.width * scale;
      const h = props.height * scale;
      // "FAST" = zlib the image stream. jsPDF stores a PNG's pixels RAW by
      // default, which turned a 17KB wordmark into ~900KB on every generated
      // document — and these get uploaded to the loan, not just downloaded.
      this.pdf.addImage(
        logo,
        props.fileType,
        MARGIN,
        this.y,
        w,
        h,
        "letterhead",
        "FAST",
      );
      return h;
    } catch (e) {
      return 0;
    }
  }

  compactHeader({ label, refNo, borrower }) {
    const right = MARGIN + CONTENT_W;
    this.sans(8, "bold").ink(T.slate);
    this.pdf.text(label.toUpperCase(), MARGIN, this.y, { charSpace: 0.9 });

    this.mono(8).ink(T.slate);
    this.pdf.text(
      [refNo != null ? `#${refNo}` : null, borrower]
        .filter(Boolean)
        .join("  ·  "),
      right,
      this.y,
      { align: "right" },
    );
    this.y += 8;
    this.pdf.setDrawColor(...T.line).setLineWidth(1);
    this.pdf.line(MARGIN, this.y, right, this.y);
    this.y += 20;
  }

  // ── metrics band (§5) ───────────────────────────────────────────────────
  metricsBand(cells) {
    const cw = CONTENT_W / cells.length;
    const subW = cw - 24;
    const SUB_LINE_H = 8.6;
    const SUB_MAX_LINES = 2;

    // The sub-line is measured and clamped BEFORE the box is drawn, and the box
    // grows to hold it. Previously it was painted with `maxWidth`, which wraps
    // downward without bound — a long sub (an exit strategy) spilled out of the
    // band and over the sections beneath it.
    this.sans(7.2);
    const subLines = cells.map((cell) =>
      cell.sub ? this.clampLines(cell.sub, subW, SUB_MAX_LINES) : [],
    );
    const maxSubLines = Math.max(1, ...subLines.map((l) => l.length));
    const h = 47 + maxSubLines * SUB_LINE_H;

    this.ensure(h + 20);

    this.pdf.setDrawColor(...T.line).setLineWidth(1);
    this.pdf.roundedRect(MARGIN, this.y, CONTENT_W, h, 6, 6, "S");

    cells.forEach((cell, i) => {
      const x = MARGIN + cw * i;
      if (i > 0) this.pdf.line(x, this.y, x, this.y + h);

      this.sans(6.8, "bold").ink(T.slate);
      this.pdf.text(cell.label.toUpperCase(), x + 14, this.y + 17, {
        charSpace: 0.7,
      });

      this.mono(14, "bold").ink(cell.color || T.ink);
      this.pdf.text(cell.value, x + 14, this.y + 38);

      if (subLines[i].length) {
        this.sans(7.2).ink(T.slate);
        this.pdf.text(subLines[i], x + 14, this.y + 51, {
          lineHeightFactor: SUB_LINE_H / 7.2,
        });
      }
    });

    this.y += h + 24;
  }

  // ── sections (§3) ───────────────────────────────────────────────────────
  /**
   * `SECTION_LEAD` is the air above a section title. It's applied only when
   * something precedes the section on this page — at the top of a page the
   * header has already set the margin, and adding it there would just push the
   * first section down out of alignment with every other page's first section.
   */
  section(title, count) {
    if (this.y > this.pageTop + 1) this.y += SECTION_LEAD;
    this.ensure(46);
    this.sans(9, "bold").ink(T.ink);
    this.pdf.text(title.toUpperCase(), MARGIN, this.y, { charSpace: 1 });

    if (count) {
      // getTextWidth doesn't know about charSpace, so add it back per character
      // or the count lands on top of a long title.
      const heading = title.toUpperCase();
      const w = this.pdf.getTextWidth(heading) + heading.length * 1 + 10;
      this.mono(8).ink(T.slate);
      this.pdf.text(count, MARGIN + w, this.y);
    }

    this.y += 6;
    this.pdf.setDrawColor(...T.ink).setLineWidth(1);
    this.pdf.line(MARGIN, this.y, MARGIN + CONTENT_W, this.y);
    // Tight to the rule. A section's body belongs to its title, so the gap
    // under the rule must stay smaller than SECTION_LEAD above it — otherwise
    // the first row floats between two sections instead of below one. Note the
    // body adds its own ROW_PAD on top of this.
    this.y += 8;
  }

  /**
   * Set the face a column value is drawn in, and wrap to it — courier is much
   * wider than helvetica, so splitting with the wrong one mis-wraps.
   *
   * Both faces are BOLD. `mono` picks the face (machine-ish values — money,
   * dates, phone, IDs — are monospaced per §4.1) and nothing else: when only
   * the sans values were bold, a row like "Marital status / Married" looked
   * emphasised next to "Date of birth / Feb 5, 1977", which said something
   * about the data that isn't true.
   */
  setValueFace(mono) {
    if (mono) this.mono(8.5, "bold");
    else this.sans(8.5, "bold");
    return this;
  }

  fieldValueLines({ value, mono = false, w }) {
    this.setValueFace(mono);
    return this.clampLines(value ?? EMPTY, w - LABEL_W, MAX_COL_LINES);
  }

  fieldRowHeight(field, w) {
    return this.fieldValueLines({ ...field, w }).length * LINE_H + ROW_PAD * 2;
  }

  /**
   * One label/value row inside a packed column. Returns the height it consumed
   * so the caller can advance that column's own cursor. `h` overrides that
   * height for the divider only, so paired rows rule off at the same baseline.
   */
  fieldRow({ label, value, mono = false, x, w, y, divider = true, h: forcedH }) {
    const lines = this.fieldValueLines({ value, mono, w });
    const h = lines.length * LINE_H + ROW_PAD * 2;

    this.sans(8.5).ink(T.slate);
    this.pdf.text(label, x, y + ROW_PAD + 8);

    // Re-set: drawing the label above left helvetica regular selected.
    this.setValueFace(mono);
    const blank = (value ?? EMPTY) === EMPTY;
    this.ink(blank ? T.slate : T.ink);
    this.pdf.text(lines, x + LABEL_W, y + ROW_PAD + 8);

    if (divider) {
      const ruleY = y + (forcedH ?? h);
      this.pdf.setDrawColor(...T.faint).setLineWidth(0.75);
      this.pdf.line(x, ruleY, x + w, ruleY);
    }
    return h;
  }

  /**
   * §4.3 — on paper, rows split into two columns with a 28pt gutter. Grouping
   * is semantic (contact left, identity right), so the caller supplies two
   * lists rather than one that gets alternated.
   *
   * Laid out a pair at a time so the page break can happen BETWEEN rows: the
   * previous version measured only the first few rows up front and then drew
   * both columns straight past the bottom margin whenever a value wrapped.
   */
  fieldColumns(left, right) {
    const rows = Math.max(left.length, right.length);
    const rightX = MARGIN + COL_W + COL_GUTTER;

    // The rule down the gutter, splitting the section's two columns. Drawn per
    // PAGE SEGMENT rather than once for the whole block: a section that breaks
    // across pages would otherwise get one line running from where it started
    // to where it ended, straight through the footer and the next page header.
    const gutterX = MARGIN + COL_W + COL_GUTTER / 2;
    const paired = left.length > 0 && right.length > 0;
    let segmentTop = this.y;
    const closeSegment = (bottom) => {
      if (!paired || bottom - segmentTop < 4) return;
      this.pdf.setDrawColor(...T.line).setLineWidth(0.75);
      this.pdf.line(gutterX, segmentTop, gutterX, bottom);
    };

    for (let i = 0; i < rows; i += 1) {
      const l = left[i];
      const r = right[i];
      const h = Math.max(
        l ? this.fieldRowHeight(l, COL_W) : 0,
        r ? this.fieldRowHeight(r, COL_W) : 0,
      );

      const yBefore = this.y;
      this.ensure(h);
      // `ensure` broke the page — end the rule where the last row did and start
      // a new one under the continuation header.
      if (this.y !== yBefore) {
        closeSegment(yBefore);
        segmentTop = this.y;
      }

      if (l) {
        this.fieldRow({
          ...l,
          x: MARGIN,
          w: COL_W,
          y: this.y,
          h,
          divider: i < left.length - 1,
        });
      }
      if (r) {
        this.fieldRow({
          ...r,
          x: rightX,
          w: COL_W,
          y: this.y,
          h,
          divider: i < right.length - 1,
        });
      }
      this.y += h;
    }

    closeSegment(this.y);
    this.y += BLOCK_TAIL;
  }

  /**
   * A full-width label-over-value row for prose that has no business inside a
   * 146pt column — exit strategy, experience summary. Blank paragraphs in the
   * source text are preserved, and the block paginates line by line so a long
   * answer flows onto the next page instead of off this one.
   */
  blockField({ label, value }) {
    this.ensure(30);
    this.sans(8.5).ink(T.slate);
    this.pdf.text(label, MARGIN, this.y + ROW_PAD + 8);
    this.y += ROW_PAD + 14;

    this.paragraphs(String(value ?? EMPTY).split(/\n{2,}/), {
      size: 8.5,
      style: "bold",
      lineH: LINE_H,
      gap: 6,
    });
    this.y += BLOCK_TAIL - 6;
  }

  // ── prose blocks ────────────────────────────────────────────────────────
  /**
   * Print wrapped text one line at a time, breaking pages between lines. The
   * font is re-set inside the loop because `break()` draws a continuation
   * header, which leaves jsPDF in a different face and size.
   */
  paragraph(
    text,
    { size = 9, style = "normal", lineH = 12.5, gap = 9, color = T.ink } = {},
  ) {
    this.sans(size, style);
    const lines = this.pdf.splitTextToSize(String(text ?? ""), CONTENT_W);

    lines.forEach((line) => {
      this.ensure(lineH);
      this.sans(size, style).ink(color);
      this.pdf.text(line, MARGIN, this.y + lineH - 3.5);
      this.y += lineH;
    });
    this.y += gap;
  }

  paragraphs(list, opts) {
    (list || []).filter(Boolean).forEach((p) => this.paragraph(p, opts));
  }

  /**
   * The signed attestation. `signature` is the PNG data URL the public form
   * captured; when it isn't on the record — the list projection drops it, and
   * legacy migrated submissions never had one — the typed name stands in and
   * `note` says so rather than implying an image we don't hold.
   */
  signatureBlock({ label, signature, name, date, note }) {
    const RULE_W = 250;
    this.ensure(120);

    if (label) {
      this.sans(8.5, "bold").ink(T.slate);
      this.pdf.text(label, MARGIN, this.y + 8);
      this.y += 16;
    }

    let drew = false;
    if (signature) {
      try {
        const props = this.pdf.getImageProperties(signature);
        const maxW = RULE_W - 8;
        const maxH = 56;
        const scale = Math.min(maxW / props.width, maxH / props.height, 1);
        const w = props.width * scale;
        const h = props.height * scale;
        this.pdf.addImage(signature, props.fileType, MARGIN + 4, this.y, w, h);
        this.y += h + 4;
        drew = true;
      } catch (e) {
        drew = false;
      }
    }

    if (!drew) {
      // Italic serif-ish stand-in for a hand — it must read as a typed record of
      // the signature, not as a facsimile of one.
      this.sans(17, "italic").ink(T.ink);
      this.pdf.text(name || EMPTY, MARGIN + 4, this.y + 28);
      this.y += 36;
    }

    this.pdf.setDrawColor(...T.line).setLineWidth(0.75);
    this.pdf.line(MARGIN, this.y, MARGIN + RULE_W, this.y);
    this.y += 13;

    this.sans(9, "bold").ink(T.ink);
    this.pdf.text(name || EMPTY, MARGIN, this.y);
    this.y += 12;

    if (date) {
      this.mono(8.5).ink(T.slate);
      this.pdf.text(date, MARGIN, this.y);
      this.y += 12;
    }

    if (note) {
      this.y += 4;
      this.paragraph(note, {
        size: 7.6,
        style: "italic",
        lineH: 10,
        gap: 0,
        color: T.slate,
      });
    }

    this.y += 8;
  }

  // ── deal economics (§6) ─────────────────────────────────────────────────
  economics(bars) {
    this.ensure(bars.length * 22 + 16);
    const labelW = 118;
    const valueW = 76;
    const trackX = MARGIN + labelW;
    const trackW = CONTENT_W - labelW - valueW;

    bars.forEach((bar) => {
      const midY = this.y + 6;

      this.sans(8).ink(T.slate);
      this.pdf.text(bar.label, MARGIN, midY + 2);

      this.pdf.setFillColor(...T.track);
      this.pdf.roundedRect(trackX, this.y + 1, trackW, 6, 3, 3, "F");

      const fraction = Math.max(0, Math.min(bar.fraction ?? 0, 1));
      if (fraction > 0) {
        this.pdf.setFillColor(...bar.color);
        this.pdf.roundedRect(
          trackX,
          this.y + 1,
          trackW * fraction,
          6,
          3,
          3,
          "F",
        );
      }

      this.mono(8.5, "bold").ink(bar.valueColor || T.ink);
      this.pdf.text(bar.value, MARGIN + CONTENT_W, midY + 2, {
        align: "right",
      });

      this.y += 20;
    });

    this.y += BLOCK_TAIL - 6;
  }

  // ── documents table (§7.3) ──────────────────────────────────────────────
  documentsTable(rows) {
    const cols = { idx: MARGIN, type: MARGIN + 30, name: MARGIN + 74 };
    const sizeRight = MARGIN + CONTENT_W;
    const nameW = CONTENT_W - 74 - 60;

    this.ensure(40);
    this.sans(7, "bold").ink(T.slate);
    this.pdf.text("#", cols.idx, this.y, { charSpace: 0.6 });
    this.pdf.text("TYPE", cols.type, this.y, { charSpace: 0.6 });
    this.pdf.text("FILE NAME", cols.name, this.y, { charSpace: 0.6 });
    this.pdf.text("SIZE", sizeRight, this.y, {
      align: "right",
      charSpace: 0.6,
    });
    this.y += 5;
    this.pdf.setDrawColor(...T.line).setLineWidth(1);
    this.pdf.line(MARGIN, this.y, MARGIN + CONTENT_W, this.y);
    this.y += 4;

    rows.forEach((row, i) => {
      this.sans(8.5);
      const lines = this.pdf.splitTextToSize(row.name, nameW);
      const h = Math.max(lines.length * LINE_H, 13) + 8;
      this.ensure(h);

      if (i % 2 === 1) {
        this.pdf.setFillColor(...T.zebra);
        this.pdf.rect(MARGIN, this.y, CONTENT_W, h, "F");
      }

      const baseline = this.y + 12;

      this.mono(8).ink(T.slate);
      this.pdf.text(String(i + 1).padStart(2, "0"), cols.idx, baseline);

      this.typeTag(row.tag, cols.type, this.y + 3);

      this.sans(8.5).ink(T.ink);
      this.pdf.text(lines, cols.name, baseline);

      this.mono(8).ink(T.slate);
      this.pdf.text(row.size, sizeRight, baseline, { align: "right" });

      this.y += h;
    });

    this.y += BLOCK_TAIL;
  }

  typeTag(tag, x, y) {
    const tone = {
      PDF: [T.red, T.redTint],
      JPG: [T.blue, T.blueTint],
      XLS: [T.pine, T.pineTint],
    }[tag] || [T.slate, T.paper];

    this.sans(6.8, "bold");
    const w = 30;
    this.pdf.setFillColor(...tone[1]);
    this.pdf.roundedRect(x, y, w, 12, 3, 3, "F");
    this.ink(tone[0]);
    this.pdf.text(tag, x + w / 2, y + 8.4, { align: "center", charSpace: 0.4 });
  }

  // ── prose ───────────────────────────────────────────────────────────────
  disclaimer(text) {
    this.ensure(46);
    const pad = 12;
    this.sans(7.6);
    const lines = this.pdf.splitTextToSize(text, CONTENT_W - pad * 2);
    const h = lines.length * 10 + pad * 2 - 4;

    this.pdf.setFillColor(...T.paper);
    this.pdf.roundedRect(MARGIN, this.y, CONTENT_W, h, 6, 6, "F");
    this.ink(T.slate);
    this.pdf.text(lines, MARGIN + pad, this.y + pad + 5);
    this.y += h + 12;
  }

  note(text) {
    this.ensure(20);
    this.sans(8.5, "italic").ink(T.slate);
    this.pdf.text(text, MARGIN, this.y + 4);
    this.y += 20;
  }

  // ── footers (§10) ───────────────────────────────────────────────────────
  /** Added last, so the page count is final. */
  stampFooters(generatedAt) {
    const total = this.pdf.getNumberOfPages();
    const yFoot = PAGE.h - 32;

    for (let p = 1; p <= total; p += 1) {
      this.pdf.setPage(p);
      this.pdf.setDrawColor(...T.line).setLineWidth(0.75);
      this.pdf.line(MARGIN, yFoot - 12, MARGIN + CONTENT_W, yFoot - 12);

      this.sans(7).ink(T.slate);
      this.pdf.text(this.identity, MARGIN, yFoot);

      this.mono(7).ink(T.slate);
      this.pdf.text(
        `Generated ${generatedAt}  ·  Page ${p} of ${total}`,
        MARGIN + CONTENT_W,
        yFoot,
        { align: "right" },
      );
    }
  }

  save(fileName) {
    this.pdf.save(fileName);
  }

  /** The same bytes `save` writes to disk, for uploading instead. */
  blob() {
    return this.pdf.output("blob");
  }
}
